import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from './Button';

interface OnboardingStep {
  title: string;
  description: string;
  image?: string;
  icon?: React.ReactNode;
  tips?: string[];
}

interface OnboardingFlowProps {
  steps: OnboardingStep[];
  onComplete: () => void;
  onSkip: () => void;
  isOpen: boolean;
}

/**
 * OnboardingFlow Component
 * 
 * A beautiful, step-by-step onboarding flow that reduces cognitive load
 * by introducing features gradually with clear explanations.
 * 
 * Features:
 * - Step-by-step progression
 * - Visual feedback
 * - Skip option
 * - Progress indicator
 * - Smooth animations
 * 
 * @example
 * <OnboardingFlow
 *   steps={[
 *     { title: 'Welcome', description: 'Get started with Khiimori', tips: [...] },
 *     { title: 'Tab Intelligence', description: 'AI-powered organization', tips: [...] },
 *   ]}
 *   onComplete={() => console.log('Completed')}
 *   onSkip={() => console.log('Skipped')}
 *   isOpen={true}
 * />
 */
export function OnboardingFlow({ 
  steps, 
  onComplete, 
  onSkip,
  isOpen 
}: OnboardingFlowProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 modal-backdrop"
            onClick={onSkip}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="relative w-full max-w-2xl rounded-3xl overflow-hidden bg-khiimori-bg-elevated/90 backdrop-blur-3xl border border-primary-500/20"
            >
              {/* Close button */}
              <button
                onClick={onSkip}
                className="absolute top-6 right-6 z-10 p-2 rounded-xl bg-khiimori-bg-elevated/50 hover:bg-khiimori-bg-elevated/80 text-khiimori-text-primary/60 hover:text-khiimori-text-primary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Progress bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-khiimori-bg-elevated/5">
                  <motion.div
                    className="h-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                    style={{
                      background: 'linear-gradient(90deg, var(--primary-500), var(--info-500))',
                    }}
                  />
                </div>

              {/* Content */}
              <div className="p-12">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Icon */}
                    {currentStepData.icon && (
                      <div 
                        className="w-16 h-16 rounded-2xl mb-6 flex items-center justify-center bg-primary-500/20"
                      >
                        {currentStepData.icon}
                      </div>
                    )}

                    {/* Title */}
                    <h2 className="text-3xl font-bold text-khiimori-text-primary mb-4">
                      {currentStepData.title}
                    </h2>

                    {/* Description */}
                    <p className="text-khiimori-text-primary/70 text-lg mb-6 leading-relaxed">
                      {currentStepData.description}
                    </p>

                    {/* Tips */}
                    {currentStepData.tips && currentStepData.tips.length > 0 && (
                      <div className="space-y-3 mb-8">
                        {currentStepData.tips.map((tip, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 + index * 0.1 }}
                            className="flex items-start gap-3 p-3 rounded-xl bg-khiimori-bg-elevated/50"
                          >
                            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-500/20 flex items-center justify-center mt-0.5">
                              <Check className="w-3 h-3 text-primary-400" />
                            </div>
                            <span className="text-khiimori-text-primary/80 text-sm">{tip}</span>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8">
                  <div className="flex items-center gap-2">
                    {steps.map((_, index) => (
                      <div
                        key={index}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          index === currentStep
                            ? 'w-8 bg-primary-500'
                            : index < currentStep
                            ? 'w-2 bg-primary-500/50'
                            : 'w-2 bg-khiimori-bg-elevated/20'
                        }`}
                      />
                    ))}
                  </div>

                  <div className="flex items-center gap-3">
                    {currentStep > 0 && (
                      <Button
                        variant="ghost"
                        onClick={handlePrev}
                        leftIcon={<ChevronLeft className="w-4 h-4" />}
                      >
                        Back
                      </Button>
                    )}
                    <Button
                      variant="gradient"
                      onClick={handleNext}
                      rightIcon={
                        currentStep === steps.length - 1 ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )
                      }
                    >
                      {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * HelpPanel Component
 * 
 * A slide-out panel that provides contextual help and tips.
 * Reduces cognitive load by providing help exactly when needed.
 * 
 * @example
 * <HelpPanel
 *   isOpen={showHelp}
 *   onClose={() => setShowHelp(false)}
 *   title="Tab Intelligence"
 *   content="This feature automatically groups your tabs..."
 *   shortcuts={[
 *     { key: 'Ctrl+K', description: 'Open command palette' },
 *   ]}
 * />
 */
export function HelpPanel({
  isOpen,
  onClose,
  title,
  content,
  shortcuts,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
  shortcuts?: Array<{ key: string; description: string }>;
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 modal-backdrop"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md p-6 overflow-y-auto bg-khiimori-bg-elevated/90 backdrop-blur-3xl border-l border-primary-500/20"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-2xl font-bold text-khiimori-text-primary">{title}</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-xl bg-khiimori-bg-elevated/50 hover:bg-khiimori-bg-elevated/80 text-khiimori-text-primary/60 hover:text-khiimori-text-primary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="prose prose-invert max-w-none mb-8">
              <p className="text-khiimori-text-primary/70 leading-relaxed">{content}</p>
            </div>

            {/* Shortcuts */}
            {shortcuts && shortcuts.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-khiimori-text-primary/80 mb-3">
                  Keyboard Shortcuts
                </h3>
                <div className="space-y-2">
                  {shortcuts.map((shortcut, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-xl bg-khiimori-bg-elevated/50"
                    >
                      <span className="text-khiimori-text-primary/70 text-sm">
                        {shortcut.description}
                      </span>
                      <kbd className="px-2 py-1 text-xs font-mono text-khiimori-text-primary bg-khiimori-bg-elevated/80 rounded border border-white/20">
                        {shortcut.key}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
